import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useRef } from 'react';

import { SessionStatus } from '@/types';

import { adminAppointmentActionSession } from './adminAppointmentActionSession';
import {
  adminTimelineAppointment,
  RENDERABLE_ADMIN_LIFECYCLE_STATUSES,
} from './adminAppointmentTimeline';
import { useAppointmentClock } from './useNativeAppointmentRows';

import type { NativeTimelineAppointment } from './appointmentActionSession';
import type { AdminAppointment } from './types';
import type { Session } from '@/types';

import { useGetMomentsMemberScopeAppointmentsQuery } from '@/store/api/momentsApi';

const MEMBER_SCOPE_RANGE_START_BUCKET_MS = 60 * 60 * 1000;
const RENDERABLE_PAST_ADMIN_LIFECYCLE_STATUSES: ReadonlySet<
  AdminAppointment['lifecycleStatus']
> = new Set(['scheduled']);

const memberScopeRangeStart = (now = Date.now()): string =>
  new Date(
    Math.floor(now / MEMBER_SCOPE_RANGE_START_BUCKET_MS) *
      MEMBER_SCOPE_RANGE_START_BUCKET_MS,
  ).toISOString();

const memberScopeRangeEnd = (now = Date.now()): string =>
  new Date(
    Math.ceil(now / MEMBER_SCOPE_RANGE_START_BUCKET_MS) *
      MEMBER_SCOPE_RANGE_START_BUCKET_MS,
  ).toISOString();

export interface UseMemberScopeAppointmentRowsOptions {
  coachId?: string;
  enabled?: boolean;
  memberId: string;
  readOnly?: boolean;
  surface?: 'upcoming' | 'past';
}

export interface UseMemberScopeAppointmentRowsResult {
  adminAppointments: AdminAppointment[];
  adminAppointmentsById: Map<string, NativeTimelineAppointment>;
  adminSessions: Session[];
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isUnavailable: boolean;
  nowTick: number;
  refetch: () => void;
}

export const useMemberScopeAppointmentRows = ({
  coachId,
  enabled = true,
  memberId,
  readOnly = true,
  surface = 'upcoming',
}: UseMemberScopeAppointmentRowsOptions): UseMemberScopeAppointmentRowsResult => {
  const rangeEnd = useRef(
    surface === 'past' ? memberScopeRangeEnd() : undefined,
  );
  const rangeStart = useRef(
    surface === 'past' ? undefined : memberScopeRangeStart(),
  );
  const { currentData, isError, isLoading, isSuccess, refetch } =
    useGetMomentsMemberScopeAppointmentsQuery(
      enabled && memberId
        ? {
            memberId,
            order:
              surface === 'past'
                ? 'current_start_at_desc,appointment_id_desc'
                : undefined,
            rangeEnd: rangeEnd.current,
            rangeStart: rangeStart.current,
          }
        : skipToken,
    );
  const nowTick = useAppointmentClock(enabled);

  const memberMatchedAppointments = useMemo(
    () =>
      (currentData?.appointments ?? []).filter(
        (appointment) =>
          appointment.expectedIdentities.member.railsUserId === memberId,
      ),
    [currentData, memberId],
  );

  const adminAppointments = useMemo(
    () =>
      memberMatchedAppointments.filter((appointment) =>
        readOnly || !coachId
          ? true
          : appointment.expectedIdentities.coach.railsUserId === coachId,
      ),
    [coachId, memberMatchedAppointments, readOnly],
  );

  const latestAdminAppointments = useMemo(() => {
    const appointmentsById = new Map<string, AdminAppointment>();
    adminAppointments.forEach((appointment) => {
      const existing = appointmentsById.get(appointment.id);
      if (!existing || appointment.version > existing.version) {
        appointmentsById.set(appointment.id, appointment);
      }
    });
    return Array.from(appointmentsById.values());
  }, [adminAppointments]);

  const adminSessions = useMemo(
    () =>
      latestAdminAppointments
        .filter(
          (appointment) =>
            appointment.bookingStatus === 'confirmed' &&
            (surface === 'past'
              ? RENDERABLE_PAST_ADMIN_LIFECYCLE_STATUSES
              : RENDERABLE_ADMIN_LIFECYCLE_STATUSES
            ).has(appointment.lifecycleStatus) &&
            (surface === 'past'
              ? Date.parse(appointment.currentSchedule.endAt) < nowTick
              : Date.parse(appointment.currentSchedule.endAt) >= nowTick),
        )
        .map((appointment) => {
          const session = adminAppointmentActionSession(appointment, {
            readOnly,
          });
          if (surface !== 'past') return session;
          return {
            ...session,
            location: undefined,
            sourceAppointmentCancelAllowed: false,
            sourceAppointmentRescheduleAllowed: false,
            status: SessionStatus.DONE,
          };
        }),
    [latestAdminAppointments, nowTick, readOnly, surface],
  );

  const adminAppointmentsById = useMemo(() => {
    const appointmentsById = new Map<string, NativeTimelineAppointment>();
    latestAdminAppointments.forEach((appointment) => {
      appointmentsById.set(
        appointment.id,
        adminTimelineAppointment(appointment, { readOnly }),
      );
    });
    return appointmentsById;
  }, [latestAdminAppointments, readOnly]);

  const allReturnedAppointmentsFilteredOut =
    (currentData?.appointments.length ?? 0) > 0 &&
    memberMatchedAppointments.length === 0;

  return {
    adminAppointments,
    adminAppointmentsById,
    adminSessions,
    isError,
    isLoading,
    isSuccess,
    isUnavailable:
      currentData?.nativeUnavailable === true ||
      allReturnedAppointmentsFilteredOut,
    nowTick,
    refetch,
  };
};
